import { Reducer } from "redux";
import { OrganizationUISettings } from "../../model/OrganizationUISettings";
import { AdminErrorListResponseData } from "../../service/api-response/AdminFormErrorResponse";
import * as organizationActions from "./organizationActions";
import { OrganizationListItem } from "./model/OrganizationListSuccessResponse";
import { Organization } from "./model/OrganizationSuccessResponse";

export interface OrganizationState {
  settings: {
    [organizationKeyName: string]: OrganizationUISettings;
  };
  isLoadingSettings: {
    [organizationKeyName: string]: boolean;
  };
  isUpsertingSettings: {
    [organizationKeyName: string]: boolean;
  };
  upsertErrors: {
    [organizationKeyName: string]: AdminErrorListResponseData | undefined;
  };
  allOrganizations: OrganizationListItem[];
  isLoadingOrganization: {
    [organizationKeyName: string]: boolean;
  };
  organization: {
    [orgKeyName: string]: undefined | Organization;
  };
  upsertOrganizationErrors: {
    [organizationKeyName: string]: AdminErrorListResponseData | undefined;
  };
}

const initialState: OrganizationState = {
  settings: {},
  isLoadingSettings: {},
  isUpsertingSettings: {},
  upsertErrors: {},
  allOrganizations: [],
  isLoadingOrganization: {},
  organization: {},
  upsertOrganizationErrors: {},
};

type SupportedActions =
  | organizationActions.GetUISettingsAction
  | organizationActions.GetUISettingsSuccessAction
  | organizationActions.GetUISettingsFailAction
  | organizationActions.UpsertUISettingsAction
  | organizationActions.UpsertUISettingsSuccessAction
  | organizationActions.UpsertUISettingsFailAction
  | organizationActions.ListOrganizationsSuccessAction
  | organizationActions.GetOrganizationAction
  | organizationActions.GetOrganizationFailAction
  | organizationActions.GetOrganizationSuccessAction
  | organizationActions.UpsertOrganizationAction
  | organizationActions.UpsertOrganizationFailAction
  | organizationActions.UpsertOrganizationSuccessAction;

export const organizationReducer: Reducer<
  OrganizationState,
  SupportedActions
> = (state = initialState, action): OrganizationState => {
  switch (action.type) {
    case organizationActions.GET_UI_SETTINGS: {
      return {
        ...state,
        isLoadingSettings: {
          ...state.isLoadingSettings,
          [action.organization]: true,
        },
      };
    }
    case organizationActions.GET_UI_SETTINGS_SUCCESS: {
      return {
        ...state,
        settings: {
          [action.organization]: action.data,
        },
        isLoadingSettings: {
          ...state.isLoadingSettings,
          [action.organization]: false,
        },
      };
    }
    case organizationActions.GET_UI_SETTINGS_FAIL: {
      return {
        ...state,
        isLoadingSettings: {
          ...state.isLoadingSettings,
          [action.organization]: false,
        },
      };
    }
    case organizationActions.UPSERT_UI_SETTINGS: {
      return {
        ...state,
        isUpsertingSettings: {
          ...state.isUpsertingSettings,
          [action.organization]: true,
        },
        upsertErrors: {
          [action.organization]: undefined,
        },
      };
    }
    case organizationActions.UPSERT_UI_SETTINGS_FAIL: {
      let newState = {
        ...state,
        isUpsertingSettings: {
          ...state.isUpsertingSettings,
          [action.organization]: false,
        },
      };

      if (action.errors && action.errors.length) {
        newState.upsertErrors[action.organization] = action.errors;
      }

      return newState;
    }
    case organizationActions.UPSERT_UI_SETTINGS_SUCCESS: {
      return {
        ...state,
        isUpsertingSettings: {
          ...state.isUpsertingSettings,
          [action.organization]: false,
        },
      };
    }
    case organizationActions.LIST_ORGANIZATIONS_SUCCESS: {
      return {
        ...state,
        allOrganizations: action.data,
      };
    }

    case organizationActions.GET_ORGANIZATION: {
      return {
        ...state,
        isLoadingOrganization: {
          ...state.isLoadingOrganization,
          [action.organization]: true,
        },
      };
    }
    case organizationActions.GET_ORGANIZATION_FAIL: {
      return {
        ...state,
        isLoadingOrganization: {
          ...state.isLoadingOrganization,
          [action.organization]: false,
        },
      };
    }
    case organizationActions.GET_ORGANIZATION_SUCCESS: {
      return {
        ...state,
        isLoadingOrganization: {
          ...state.isLoadingOrganization,
          [action.organization]: false,
        },
        organization: {
          ...state.organization,
          [action.organization]: action.data,
        },
      };
    }
    case organizationActions.UPSERT_ORGANIZATION: {
      return {
        ...state,
        upsertOrganizationErrors: {
          ...state.upsertOrganizationErrors,
          [action.organizationKeyName || "__new"]: undefined,
        },
      };
    }
    case organizationActions.UPSERT_ORGANIZATION_FAIL: {
      return {
        ...state,
        upsertOrganizationErrors: {
          ...state.upsertOrganizationErrors,
          [action.organizationKeyName || "__new"]: action.errors,
        },
      };
    }
    case organizationActions.UPSERT_ORGANIZATION_SUCCESS: {
      return {
        ...state,
        upsertOrganizationErrors: {
          ...state.upsertOrganizationErrors,
          [action.organizationKeyName || "__new"]: undefined,
        },
      };
    }

    default: {
      return state;
    }
  }
};
